import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardList, Filter, ChevronDown, ChevronRight, Search, Loader2, RefreshCw, Clock, Download, Calendar, X, Building, Building2, MapPin, Eye, Check, XCircle, AlertTriangle, CalendarClock } from 'lucide-react';
import { exportReportToCSV, getDateFilename } from '../../../utils/exportUtils';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import { reportsEarningsService } from '../../../services/reportsEarningsService';
import { overtimeService } from '../../../services/overtimeService';
import { useCorporateDashboardRealtime } from '../../../hooks/useRealtimeSubscription';
import ReportDetailModal from '../../../components/corporate/reports/ReportDetailModal';
import RejectModal from '../../../components/corporate/reports/RejectModal';
import DisputeModal from '../../../components/corporate/reports/DisputeModal';
import OvertimeApprovalModal from '../../../components/corporate/reports/OvertimeApprovalModal';
import JobReport from '../../../components/corporate/reports/JobReport';
import ExtensionRequestsPanel from '../../../components/corporate/reports/ExtensionRequestsPanel';
import { jobService } from '../../../services/jobService';
import { cn } from '../../../utils/cn';

// Helper to transform snake_case job data to camelCase for JobReport component
const transformJobForReport = (job, instances = []) => {
  if (!job) return null;
  return {
    id: job.id,
    jobDisplayId: job.job_display_id || `JOB-${String(job.id).slice(-6).toUpperCase()}`,
    jobName: job.job_name,
    jobType: job.job_type,
    description: job.description,
    priority: job.priority,
    status: job.status,
    locationAddress: job.location_address,
    paymentType: job.payment_type,
    paymentAmount: job.payment_amount,
    estimatedHours: job.estimated_hours,
    scheduledDate: job.scheduled_date,
    scheduledTime: job.scheduled_time,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    // Job configuration fields - used to determine if features were required
    photosRequiredBefore: job.photos_required_before,
    photosRequiredAfter: job.photos_required_after,
    checklistTemplate: job.checklist_template,
    questionnaireTemplate: job.questionnaire_template,
    clockInRadius: job.clock_in_radius,
    // Safety, Equipment, Other Details
    safetyRequirements: job.safety_requirements,
    equipmentList: job.equipment_list,
    otherDetails: job.other_details,
    // Partner agency
    jobSource: job.job_source,
    partnerAgency: job.partner_agency,
    serviceReceiver: job.service_receiver ? {
      id: job.service_receiver.id,
      receiver_name: job.service_receiver.receiver_name,
      receiver_type: job.service_receiver.receiver_type,
      contact_person: job.service_receiver.contact_person,
      contact_email: job.service_receiver.contact_email,
      contact_phone: job.service_receiver.contact_phone,
      address: job.service_receiver.address
    } : null,
    recurringSchedule: job.recurring_schedule,
    job_instances: instances.map(inst => ({
      id: inst.id,
      instance_number: inst.instance_number,
      scheduled_date: inst.scheduled_date,
      scheduled_time: inst.scheduled_time,
      status: inst.status,
      contractor: inst.contractor
    }))
  };
};

const transformInstanceForReport = (instance) => {
  if (!instance) return null;
  return {
    id: instance.id,
    instance_number: instance.instance_number,
    scheduled_date: instance.scheduled_date,
    scheduled_time: instance.scheduled_time,
    status: instance.status,
    contractor: instance.contractor
  };
};

// Helper to format decimal hours as "Xhr Ymn"
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0hr 0mn';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}hr ${minutes}mn`;
};

// Tab configuration - Paid tab removed since payment recording should only happen from Earnings page
const TABS = [
  { key: 'all', label: 'All', statusFilter: null },
  { key: 'submitted', label: 'Pending Review', statusFilter: 'submitted' },
  { key: 'overtime', label: 'Overtime Requests', statusFilter: null, isOvertime: true },
  { key: 'extensions', label: 'Extension Requests', statusFilter: null, isExtensions: true },
  { key: 'approved', label: 'Approved', statusFilter: 'approved' },
  { key: 'disputed', label: 'Disputed', statusFilter: 'disputed' }
];

// Period filter options
const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom Range' }
];

// Helper to calculate date range based on period selection
const getDateRangeForPeriod = (period) => {
  const now = new Date();
  let startDate = null;
  let endDate = null;

  switch (period) {
    case 'current_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'last_6_months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      endDate = now;
      break;
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }

  return {
    startDate: startDate?.toISOString()?.split('T')[0],
    endDate: endDate?.toISOString()?.split('T')[0]
  };
};

// Color constants for alternating job site groups (matching Job Management)
const JOBSITE_COLORS = {
  odd: { jobSite: 'bg-background', job: 'bg-surface', instance: 'bg-surface-light' },
  even: { jobSite: 'bg-background-alt', job: 'bg-surface-alt', instance: 'bg-surface-alt-light' }
};

// Get timesheet status color - paid status removed since it should only be viewed in Earnings page
const getTimesheetStatusColor = (status) => {
  const colors = {
    submitted: 'bg-orange-500/20 text-orange-300',
    approved: 'bg-green-500/20 text-green-300',
    disputed: 'bg-red-500/20 text-red-300',
    rejected: 'bg-red-500/20 text-red-300',
    needs_revision: 'bg-yellow-500/20 text-yellow-300'
  };
  return colors[status] || 'bg-gray-500/20 text-text-secondary';
};

// Format status text for display
const formatStatusText = (status) => {
  if (!status) return 'Submitted';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Format date for display
const formatDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
    const year = d.getFullYear();

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${monthShort} ${day}, ${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${String(d.getMonth() + 1).padStart(2, '0')}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${monthShort} ${year}`;
    }
  } catch {
    return dateString;
  }
};

// Group instances by Client → Job Site → Job hierarchy
const groupInstancesByHierarchy = (instances) => {
  const clientGroups = new Map();

  instances?.forEach(instance => {
    const job = instance?.job;
    if (!job) return;

    // Determine client
    const clientId = job?.client_id || 'unassigned';
    const clientName = job?.client?.client_name || 'Unassigned Client';
    const clientTag = job?.client?.tag || null;
    const clientContactPerson = job?.client?.contact_person || null;

    if (!clientGroups.has(clientId)) {
      clientGroups.set(clientId, {
        client: { id: clientId, name: clientName, tag: clientTag, contactPerson: clientContactPerson, isUnassigned: clientId === 'unassigned' },
        jobSites: new Map()
      });
    }

    const clientGroup = clientGroups.get(clientId);

    // Determine job site
    const jobSiteId = job?.job_site_id || 'unassigned';
    const jobSiteName = job?.job_site?.site_name || 'No Site';
    const jobSiteAddress = job?.job_site?.address || job?.location_address || null;

    if (!clientGroup.jobSites.has(jobSiteId)) {
      clientGroup.jobSites.set(jobSiteId, {
        jobSite: { id: jobSiteId, name: jobSiteName, address: jobSiteAddress, isUnassigned: jobSiteId === 'unassigned' },
        jobs: new Map()
      });
    }

    const jobSiteGroup = clientGroup.jobSites.get(jobSiteId);

    // Group by job
    const jobId = job?.id;
    if (!jobSiteGroup.jobs.has(jobId)) {
      jobSiteGroup.jobs.set(jobId, {
        job: job,
        instances: []
      });
    }

    jobSiteGroup.jobs.get(jobId).instances.push(instance);
  });

  // Sort client groups: Unassigned last, then alphabetically
  const sortedClientGroups = Array.from(clientGroups.entries()).sort((a, b) => {
    if (a[0] === 'unassigned') return 1;
    if (b[0] === 'unassigned') return -1;
    return a[1].client.name.localeCompare(b[1].client.name);
  });

  // Sort job sites within each client
  sortedClientGroups.forEach(([, clientData]) => {
    const sortedJobSites = Array.from(clientData.jobSites.entries()).sort((a, b) => {
      if (a[0] === 'unassigned') return 1;
      if (b[0] === 'unassigned') return -1;
      return a[1].jobSite.name.localeCompare(b[1].jobSite.name);
    });
    clientData.jobSites = new Map(sortedJobSites);
  });

  return new Map(sortedClientGroups);
};

export default function Reports() {
  useAuth();
  const { currentCompany } = useCompany();
  const { preferences } = useUserPreferences();

  // Data state
  const [jobs, setJobs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractor, setSelectedContractor] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hierarchy expansion state
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [expandedJobSites, setExpandedJobSites] = useState(new Set());
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  // New filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedJobSite, setSelectedJobSite] = useState('');

  // Modal state
  const [viewingReport, setViewingReport] = useState(null);
  const [rejectingInstance, setRejectingInstance] = useState(null);
  const [disputingInstance, setDisputingInstance] = useState(null);

  // Job Report modal state
  const [viewingJobReport, setViewingJobReport] = useState(null); // { job, instances }
  const [viewingInstanceReport, setViewingInstanceReport] = useState(null); // { job, instance }

  // Overtime state
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeCount, setOvertimeCount] = useState(0);
  const [reviewingOvertime, setReviewingOvertime] = useState(null);
  const [overtimeProcessing, setOvertimeProcessing] = useState(false);

  // Extension requests state
  const [extensionCount, setExtensionCount] = useState(0);

  // Selection state for bulk actions
  const [selectedInstances, setSelectedInstances] = useState(new Set());

  // Load data
  const loadData = useCallback(async () => {
    if (!currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Always load overtime and extension counts for badges
      const [pendingOvertimeCount, extensionRequests] = await Promise.all([
        overtimeService.getPendingOvertimeCount(currentCompany.id),
        jobService.getExtensionRequests(currentCompany.id, 'pending')
      ]);
      setOvertimeCount(pendingOvertimeCount);
      setExtensionCount(extensionRequests?.length || 0);

      // If on overtime tab, load overtime requests
      if (activeTab === 'overtime') {
        const overtimeData = await overtimeService.getOvertimeRequests(currentCompany.id, { status: 'pending' });
        setOvertimeRequests(overtimeData);
        setLoading(false);
        return;
      }

      // If on extensions tab, the ExtensionRequestsPanel handles its own data loading
      if (activeTab === 'extensions') {
        setLoading(false);
        return;
      }

      // Calculate date range based on period selection
      let dateFilters = {};
      if (selectedPeriod === 'custom') {
        dateFilters = {
          startDate: customDateFrom || null,
          endDate: customDateTo || null
        };
      } else {
        dateFilters = getDateRangeForPeriod(selectedPeriod);
      }

      const filters = {
        status: activeTab === 'all' ? null : activeTab,
        contractorId: selectedContractor || null,
        clientId: selectedClient || null,
        jobSiteId: selectedJobSite || null,
        ...dateFilters
      };

      const [jobsData, counts, contractorsList] = await Promise.all([
        reportsEarningsService.getSubmittedJobs(currentCompany.id, filters),
        reportsEarningsService.getStatusCounts(currentCompany.id),
        reportsEarningsService.getContractorsWithSubmissions(currentCompany.id)
      ]);

      setJobs(jobsData);
      setStatusCounts(counts);
      setContractors(contractorsList);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError(err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, activeTab, selectedContractor, selectedPeriod, customDateFrom, customDateTo, selectedClient, selectedJobSite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time updates - refresh when timesheets or overtime requests change
  useCorporateDashboardRealtime(currentCompany?.id, () => {
    loadData();
  });

  // Extract unique clients from loaded jobs
  const clients = useMemo(() => {
    const clientMap = new Map();
    jobs?.forEach(instance => {
      const client = instance?.job?.client;
      if (client && !clientMap.has(client.id)) {
        clientMap.set(client.id, client);
      }
    });
    return Array.from(clientMap.values());
  }, [jobs]);

  // Extract unique job sites from loaded jobs
  const jobSites = useMemo(() => {
    const siteMap = new Map();
    jobs?.forEach(instance => {
      const site = instance?.job?.job_site;
      if (site && !siteMap.has(site.id)) {
        siteMap.set(site.id, site);
      }
    });
    return Array.from(siteMap.values());
  }, [jobs]);

  // Filter jobs by search term
  const filteredJobs = jobs?.filter(instance => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      instance?.job?.job_name?.toLowerCase()?.includes(search) ||
      instance?.contractor?.full_name?.toLowerCase()?.includes(search) ||
      instance?.job?.location_address?.toLowerCase()?.includes(search) ||
      instance?.job?.client?.client_name?.toLowerCase()?.includes(search) ||
      instance?.job?.job_site?.site_name?.toLowerCase()?.includes(search)
    );
  });

  // Group instances by hierarchy (Client → Job Site → Job → Instances)
  const hierarchyGroups = useMemo(() => {
    return groupInstancesByHierarchy(filteredJobs);
  }, [filteredJobs]);

  // Toggle client expansion
  const toggleClient = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  // Toggle job site expansion
  const toggleJobSite = (jobSiteKey) => {
    setExpandedJobSites(prev => {
      const next = new Set(prev);
      if (next.has(jobSiteKey)) {
        next.delete(jobSiteKey);
      } else {
        next.add(jobSiteKey);
      }
      return next;
    });
  };

  // Toggle job expansion (for instances)
  const toggleJob = (jobId) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  // Action handlers
  const handleApprove = async (instanceId) => {
    try {
      await reportsEarningsService.approveReport(instanceId);
      loadData();
    } catch (err) {
      console.error('Error approving report:', err);
      setError(err?.message || 'Failed to approve report');
    }
  };

  const handleReject = async (instanceId, reason) => {
    try {
      await reportsEarningsService.rejectReport(instanceId, reason);
      setRejectingInstance(null);
      loadData();
    } catch (err) {
      console.error('Error rejecting report:', err);
      setError(err?.message || 'Failed to reject report');
    }
  };

  const handleDispute = async (instanceId, reason, category) => {
    try {
      await reportsEarningsService.disputeReport(instanceId, reason, category);
      setDisputingInstance(null);
      loadData();
    } catch (err) {
      console.error('Error creating dispute:', err);
      setError(err?.message || 'Failed to create dispute');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedInstances.size === 0) return;
    try {
      await reportsEarningsService.bulkApprove(Array.from(selectedInstances));
      setSelectedInstances(new Set());
      loadData();
    } catch (err) {
      console.error('Error bulk approving:', err);
      setError(err?.message || 'Failed to bulk approve');
    }
  };

  // Overtime handlers
  const handleOvertimeApprove = async (approvedAmount) => {
    if (!reviewingOvertime) return;
    try {
      setOvertimeProcessing(true);
      await overtimeService.approveOvertimeRequest(reviewingOvertime.id, approvedAmount);
      setReviewingOvertime(null);
      loadData();
    } catch (err) {
      console.error('Error approving overtime:', err);
      setError(err?.message || 'Failed to approve overtime request');
    } finally {
      setOvertimeProcessing(false);
    }
  };

  const handleOvertimeReject = async (reason) => {
    if (!reviewingOvertime) return;
    try {
      setOvertimeProcessing(true);
      await overtimeService.rejectOvertimeRequest(reviewingOvertime.id, reason);
      setReviewingOvertime(null);
      loadData();
    } catch (err) {
      console.error('Error rejecting overtime:', err);
      setError(err?.message || 'Failed to reject overtime request');
    } finally {
      setOvertimeProcessing(false);
    }
  };

  // Tab counts
  const getTabCount = (tabKey) => {
    if (tabKey === 'all') return statusCounts.all || 0;
    if (tabKey === 'overtime') return overtimeCount;
    if (tabKey === 'extensions') return extensionCount;
    return statusCounts[tabKey] || 0;
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 overflow-y-auto pt-16 pl-0 lg:pl-60">
          <div className="container mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                    <ClipboardList className="w-8 h-8 text-orange-500" />
                    Reports
                  </h1>
                  <p className="text-text-secondary mt-2">Review submitted work and approve timesheets</p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Export CSV Button */}
                  {filteredJobs?.length > 0 && activeTab !== 'overtime' && (
                    <button
                      onClick={() => exportReportToCSV(filteredJobs, getDateFilename('reports'))}
                      className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-600 text-white rounded-lg transition-colors"
                    >
                      <Download size={18} />
                      Export CSV
                    </button>
                  )}
                  <button
                    onClick={loadData}
                    className="flex items-center gap-2 px-4 py-2 bg-border hover:bg-border-light text-white rounded-lg transition-colors"
                  >
                    <RefreshCw size={18} />
                    Refresh
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 px-4 py-2 bg-border hover:bg-border-light text-white rounded-lg transition-colors"
                  >
                    <Filter size={18} />
                    Filters
                    <ChevronDown size={16} className={cn('transition-transform', showFilters && 'rotate-180')} />
                  </button>
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 p-4 bg-surface border border-border rounded-lg space-y-4">
                  {/* First Row: Search and Period */}
                  <div className="flex flex-wrap gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-text-secondary mb-1">Search</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Job name, contractor, location..."
                          className="w-full pl-10 pr-4 py-2 bg-border border border-border-light rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary"
                        />
                      </div>
                    </div>

                    {/* Period Filter */}
                    <div className="min-w-[180px]">
                      <label className="block text-sm text-text-secondary mb-1">Period</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                        <select
                          value={selectedPeriod}
                          onChange={(e) => setSelectedPeriod(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary appearance-none"
                        >
                          {PERIOD_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                      </div>
                    </div>

                    {/* Custom Date Range - only show when custom is selected */}
                    {selectedPeriod === 'custom' && (
                      <>
                        <div className="min-w-[150px]">
                          <label className="block text-sm text-text-secondary mb-1">From Date</label>
                          <input
                            type="date"
                            value={customDateFrom}
                            onChange={(e) => setCustomDateFrom(e.target.value)}
                            className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary"
                          />
                        </div>
                        <div className="min-w-[150px]">
                          <label className="block text-sm text-text-secondary mb-1">To Date</label>
                          <input
                            type="date"
                            value={customDateTo}
                            onChange={(e) => setCustomDateTo(e.target.value)}
                            className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Second Row: Entity Filters */}
                  <div className="flex flex-wrap gap-4">
                    {/* Contractor Filter */}
                    <div className="min-w-[200px]">
                      <label className="block text-sm text-text-secondary mb-1">Contractor</label>
                      <select
                        value={selectedContractor}
                        onChange={(e) => setSelectedContractor(e.target.value)}
                        className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary"
                      >
                        <option value="">All Contractors</option>
                        {contractors?.map(c => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Client Filter */}
                    <div className="min-w-[200px]">
                      <label className="block text-sm text-text-secondary mb-1">Client</label>
                      <select
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary"
                      >
                        <option value="">All Clients</option>
                        {clients?.map(c => (
                          <option key={c.id} value={c.id}>{c.client_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Job Site Filter */}
                    <div className="min-w-[200px]">
                      <label className="block text-sm text-text-secondary mb-1">Job Site</label>
                      <select
                        value={selectedJobSite}
                        onChange={(e) => setSelectedJobSite(e.target.value)}
                        className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary focus:outline-none focus:border-primary"
                      >
                        <option value="">All Job Sites</option>
                        {jobSites?.map(s => (
                          <option key={s.id} value={s.id}>{s.site_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Clear Filters Button */}
                    {(selectedContractor || selectedClient || selectedJobSite || selectedPeriod !== 'all' || searchTerm) && (
                      <div className="flex items-end">
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setSelectedContractor('');
                            setSelectedClient('');
                            setSelectedJobSite('');
                            setSelectedPeriod('all');
                            setCustomDateFrom('');
                            setCustomDateTo('');
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        >
                          <X size={16} />
                          Clear Filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="mb-6">
              <div className="flex gap-2 border-b border-border pb-2 flex-wrap">
                {TABS.map(tab => {
                  const count = getTabCount(tab.key);
                  const isActive = activeTab === tab.key;
                  const isOvertimeWithPending = tab.key === 'overtime' && count > 0;
                  const isExtensionsWithPending = tab.key === 'extensions' && count > 0;
                  const hasPendingBadge = isOvertimeWithPending || isExtensionsWithPending;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={cn(
                        'px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2',
                        isActive
                          ? 'bg-orange-500 text-white'
                          : hasPendingBadge
                            ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/50'
                            : 'bg-border text-text-secondary hover:bg-border-light'
                      )}
                    >
                      {tab.key === 'overtime' && <Clock size={16} />}
                      {tab.key === 'extensions' && <CalendarClock size={16} />}
                      {tab.label}
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs',
                        isActive ? 'bg-orange-600' : hasPendingBadge ? 'bg-orange-500 text-white' : 'bg-border-light'
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedInstances.size > 0 && (
              <div className="mb-4 p-3 bg-surface border border-border rounded-lg flex items-center justify-between">
                <span className="text-text-primary">
                  {selectedInstances.size} item{selectedInstances.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  {activeTab === 'submitted' && (
                    <button
                      onClick={handleBulkApprove}
                      className="px-4 py-2 bg-accent hover:bg-accent-600 text-white rounded-lg transition-colors"
                    >
                      Approve Selected
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedInstances(new Set())}
                    className="px-4 py-2 bg-border-light hover:bg-border-light text-text-primary rounded-lg transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
            )}

            {/* Extensions Tab Content */}
            {!loading && activeTab === 'extensions' && (
              <ExtensionRequestsPanel
                companyId={currentCompany?.id}
                onUpdate={loadData}
              />
            )}

            {/* Empty State - Regular tabs */}
            {!loading && activeTab !== 'overtime' && activeTab !== 'extensions' && filteredJobs?.length === 0 && (
              <div className="text-center py-12">
                <ClipboardList className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-text-primary mb-2">No reports found</h3>
                <p className="text-text-secondary">
                  {activeTab === 'all'
                    ? 'No submitted reports yet. Reports will appear here once contractors submit their work.'
                    : `No reports with "${TABS.find(t => t.key === activeTab)?.label}" status.`}
                </p>
              </div>
            )}

            {/* Empty State - Overtime tab */}
            {!loading && activeTab === 'overtime' && overtimeRequests?.length === 0 && (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-text-primary mb-2">No overtime requests</h3>
                <p className="text-text-secondary">
                  Overtime requests from contractors will appear here when they work beyond estimated hours.
                </p>
              </div>
            )}

            {/* Overtime Requests List */}
            {!loading && activeTab === 'overtime' && overtimeRequests?.length > 0 && (
              <div className="space-y-4">
                {overtimeRequests.map(request => (
                  <div
                    key={request.id}
                    className="bg-surface border border-border rounded-lg p-4 hover:border-orange-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-text-primary">
                            {request?.job_instance?.jobs?.job_name || 'Unknown Job'}
                          </h3>
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                            {request.overtime_hours}h overtime
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-text-tertiary text-xs">Contractor</p>
                            <p className="text-text-secondary">{request?.contractor?.full_name || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-text-tertiary text-xs">Hours (Est → Actual)</p>
                            <p className="text-text-secondary">
                              {request.estimated_hours}h → {request.actual_hours}h
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-background rounded-lg">
                          <p className="text-text-tertiary text-xs mb-1">Justification</p>
                          <p className="text-text-secondary text-sm">{request.justification}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setReviewingOvertime(request)}
                        className="ml-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors whitespace-nowrap"
                      >
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Job Hierarchy Table - Agency → Service Receiver → Job → Instance */}
            {!loading && activeTab !== 'overtime' && activeTab !== 'extensions' && filteredJobs?.length > 0 && (
              <div className="bg-surface border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-surface-dark border-b border-border">
                      <th className="px-4 py-3 text-left">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={filteredJobs?.length > 0 && selectedInstances.size === filteredJobs?.length}
                            onChange={() => {
                              if (selectedInstances.size === filteredJobs?.length) {
                                setSelectedInstances(new Set());
                              } else {
                                setSelectedInstances(new Set(filteredJobs?.map(i => i?.id)));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                          />
                          <span className="text-xs font-semibold text-text-secondary">All</span>
                        </label>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">Job / Instance</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">Contractor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">Scheduled Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">Hours</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-text-secondary">Actions</th>
                    </tr>
                    {/* Gradient separator after header */}
                    <tr>
                      <td colSpan="7" className="p-0">
                        <div className="h-1 bg-gradient-to-r from-[#FF7A00] via-[#00C47D] to-[#FF7A00]" />
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(hierarchyGroups.entries()).map(([clientId, { client, jobSites }], clientIdx) => {
                      const isClientExpanded = expandedClients.has(clientId);
                      // Count total instances across all job sites/jobs for this client
                      let totalClientInstances = 0;
                      jobSites.forEach(({ jobs }) => {
                        jobs.forEach(({ instances }) => { totalClientInstances += instances.length; });
                      });

                      // Get all instance IDs for this client for selection
                      const allClientInstanceIds = [];
                      jobSites.forEach(({ jobs }) => {
                        jobs.forEach(({ instances }) => {
                          instances.forEach(inst => allClientInstanceIds.push(inst?.id));
                        });
                      });
                      const allClientInstancesSelected = allClientInstanceIds.length > 0 && allClientInstanceIds.every(id => selectedInstances.has(id));
                      const someClientInstancesSelected = allClientInstanceIds.some(id => selectedInstances.has(id));

                      return (
                        <React.Fragment key={clientId}>
                          {/* Client Separator - subtle divider between clients */}
                          {clientIdx > 0 && (
                            <tr className="h-2 bg-background">
                              <td colSpan="8" className="p-0">
                                <div className="h-px bg-border" />
                              </td>
                            </tr>
                          )}
                          {/* Client Row - Top Level */}
                          <tr
                            className={cn(
                              'bg-surface-dark cursor-pointer hover:brightness-110 transition-all',
                              client.isUnassigned ? 'border-l-4 border-l-gray-500' : 'border-l-4 border-l-primary-orange'
                            )}
                            onClick={() => toggleClient(clientId)}
                          >
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={allClientInstancesSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someClientInstancesSelected && !allClientInstancesSelected;
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedInstances(prev => {
                                    const next = new Set(prev);
                                    if (allClientInstancesSelected) {
                                      allClientInstanceIds.forEach(id => next.delete(id));
                                    } else {
                                      allClientInstanceIds.forEach(id => next.add(id));
                                    }
                                    return next;
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                              />
                            </td>
                            <td colSpan="7" className="px-4 py-4">
                              <div className="flex items-start gap-2">
                                {isClientExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-accent mt-0.5" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-text-secondary mt-0.5" />
                                )}
                                <Building className="w-5 h-5 text-primary-orange mt-0.5" />
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-text-primary">
                                      {client.name}
                                    </span>
                                    {client.tag && (
                                      <span className="px-1.5 py-0.5 bg-accent/10 text-accent text-xs rounded font-medium">
                                        {client.tag}
                                      </span>
                                    )}
                                    <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                      {totalClientInstances} report{totalClientInstances !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  {client.contactPerson && (
                                    <span className="text-xs text-text-tertiary mt-0.5">
                                      {client.contactPerson}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Job Sites within this Client */}
                          {isClientExpanded && Array.from(jobSites.entries()).map(([jobSiteId, { jobSite, jobs: siteJobs }], siteIdx) => {
                            const isOdd = siteIdx % 2 === 0;
                            const colors = isOdd ? JOBSITE_COLORS.odd : JOBSITE_COLORS.even;
                            const jobSiteKey = `${clientId}-${jobSiteId}`;
                            const isJobSiteExpanded = expandedJobSites.has(jobSiteKey);

                            // Count total instances in this job site
                            let totalSiteInstances = 0;
                            siteJobs.forEach(({ instances }) => { totalSiteInstances += instances.length; });

                            // Get all instance IDs in this job site for selection
                            const allSiteInstanceIds = [];
                            siteJobs.forEach(({ instances }) => {
                              instances.forEach(inst => allSiteInstanceIds.push(inst?.id));
                            });
                            const allSiteInstancesSelected = allSiteInstanceIds.length > 0 && allSiteInstanceIds.every(id => selectedInstances.has(id));
                            const someSiteInstancesSelected = allSiteInstanceIds.some(id => selectedInstances.has(id));

                            return (
                              <React.Fragment key={jobSiteKey}>
                                {/* Job Site Row */}
                                <tr
                                  className={cn(
                                    colors.jobSite,
                                    'cursor-pointer hover:brightness-110 transition-all'
                                  )}
                                  onClick={() => toggleJobSite(jobSiteKey)}
                                >
                                  <td className="px-4 py-3.5 pl-8">
                                    <input
                                      type="checkbox"
                                      checked={allSiteInstancesSelected}
                                      ref={el => {
                                        if (el) el.indeterminate = someSiteInstancesSelected && !allSiteInstancesSelected;
                                      }}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setSelectedInstances(prev => {
                                          const next = new Set(prev);
                                          if (allSiteInstancesSelected) {
                                            allSiteInstanceIds.forEach(id => next.delete(id));
                                          } else {
                                            allSiteInstanceIds.forEach(id => next.add(id));
                                          }
                                          return next;
                                        });
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                    />
                                  </td>
                                  <td colSpan="7" className="px-4 py-3.5">
                                    <div className="flex items-start gap-2 pl-4">
                                      {isJobSiteExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-accent mt-0.5" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-text-secondary mt-0.5" />
                                      )}
                                      <Building2 className="w-4 h-4 text-accent mt-0.5" />
                                      <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-text-primary truncate max-w-[200px]">
                                            {jobSite.name}
                                          </span>
                                          <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                            {totalSiteInstances} report{totalSiteInstances !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        {jobSite.address && (
                                          <span className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5 truncate max-w-[300px]">
                                            <MapPin className="w-3 h-3 flex-shrink-0" />
                                            {jobSite.address}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>

                                {/* Job Rows (when job site is expanded) */}
                                {isJobSiteExpanded && Array.from(siteJobs.entries()).map(([jobId, { job, instances: jobInstances }], jobIndex) => {
                                  const isJobExpanded = expandedJobs.has(jobId);
                                  const isLastJob = jobIndex === siteJobs.size - 1;
                                  const showJobDropdown = jobInstances.length > 1;

                                  // Get all instance IDs in this job for selection
                                  const jobInstanceIds = jobInstances.map(inst => inst?.id);
                                  const allJobInstancesSelected = jobInstanceIds.length > 0 && jobInstanceIds.every(id => selectedInstances.has(id));
                                  const someJobInstancesSelected = jobInstanceIds.some(id => selectedInstances.has(id));

                                  // Calculate totals for this job
                                  const totalHours = jobInstances.reduce((sum, inst) => {
                                    const timesheet = inst?.timesheet?.[0] || inst?.timesheet;
                                    return sum + (parseFloat(timesheet?.total_hours) || 0);
                                  }, 0);

                                  return (
                                    <React.Fragment key={jobId}>
                                      {/* Job Row */}
                                      <tr
                                        className={cn(
                                          colors.job,
                                          !isLastJob && 'border-b border-dashed border-accent/40',
                                          showJobDropdown && 'cursor-pointer hover:brightness-110',
                                          'transition-all'
                                        )}
                                        onClick={() => showJobDropdown && toggleJob(jobId)}
                                      >
                                        {/* Checkbox */}
                                        <td className="px-4 py-1.5">
                                          <input
                                            type="checkbox"
                                            checked={allJobInstancesSelected}
                                            ref={(el) => { if (el) el.indeterminate = someJobInstancesSelected && !allJobInstancesSelected; }}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              setSelectedInstances(prev => {
                                                const next = new Set(prev);
                                                if (allJobInstancesSelected) {
                                                  jobInstanceIds.forEach(id => next.delete(id));
                                                } else {
                                                  jobInstanceIds.forEach(id => next.add(id));
                                                }
                                                return next;
                                              });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                          />
                                        </td>
                                        {/* Job Name with instance count */}
                                        <td className="px-4 py-1.5 pl-16">
                                          <div className="flex items-center gap-2">
                                            {showJobDropdown && (
                                              isJobExpanded ? (
                                                <ChevronDown className="w-3 h-3 text-accent" />
                                              ) : (
                                                <ChevronRight className="w-3 h-3 text-text-secondary" />
                                              )
                                            )}
                                            <span className="font-medium text-text-primary text-sm">{job?.job_name}</span>
                                            {showJobDropdown && (
                                              <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                                {jobInstances.length} instance{jobInstances.length !== 1 ? 's' : ''}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        {/* Contractor (show if single instance) */}
                                        <td className="px-4 py-1.5 text-sm text-text-secondary">
                                          {!showJobDropdown && jobInstances[0]?.contractor?.full_name}
                                        </td>
                                        {/* Status (aggregate or single) */}
                                        <td className="px-4 py-1.5">
                                          {!showJobDropdown ? (
                                            <span className={cn(
                                              'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                              getTimesheetStatusColor(jobInstances[0]?.status)
                                            )}>
                                              {formatStatusText(jobInstances[0]?.status)}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-text-secondary">Various</span>
                                          )}
                                        </td>
                                        {/* Date */}
                                        <td className="px-4 py-1.5 text-sm text-text-secondary">
                                          {!showJobDropdown && formatDate(jobInstances[0]?.scheduled_date, preferences?.date_format)}
                                        </td>
                                        {/* Hours */}
                                        <td className="px-4 py-1.5 text-sm text-text-secondary">
                                          {formatHoursMinutes(totalHours)}
                                        </td>
                                        {/* Actions */}
                                        <td className="px-4 py-1.5">
                                          <div className="flex items-center justify-end gap-1">
                                            {!showJobDropdown && (
                                              <>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingReport(jobInstances[0]);
                                                  }}
                                                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                                                  title="View report"
                                                >
                                                  <Eye className="w-4 h-4 text-text-primary" />
                                                </button>
                                                {(jobInstances[0]?.timesheet?.[0]?.status === 'submitted' || jobInstances[0]?.status === 'submitted') && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(jobInstances[0]?.id);
                                                      }}
                                                      className="p-1.5 hover:bg-green-500/20 rounded transition-colors"
                                                      title="Approve"
                                                    >
                                                      <Check className="w-4 h-4 text-green-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRejectingInstance(jobInstances[0]);
                                                      }}
                                                      className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                                                      title="Reject"
                                                    >
                                                      <XCircle className="w-4 h-4 text-red-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisputingInstance(jobInstances[0]);
                                                      }}
                                                      className="p-1.5 hover:bg-yellow-500/20 rounded transition-colors"
                                                      title="Dispute"
                                                    >
                                                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                                    </button>
                                                  </>
                                                )}
                                              </>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setViewingJobReport({ job, instances: jobInstances });
                                              }}
                                              className="p-1.5 hover:bg-white/10 rounded transition-colors"
                                              title="View job report"
                                            >
                                              <ClipboardList className="w-4 h-4 text-text-secondary" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>

                                      {/* Instance Rows (when job is expanded and has multiple instances) */}
                                      {isJobExpanded && showJobDropdown && jobInstances.map((instance, instIndex) => {
                                        const timesheet = instance?.timesheet?.[0] || instance?.timesheet;
                                        const status = instance?.status; // Use instance status as source of truth
                                        const isLastInstance = instIndex === jobInstances.length - 1;

                                        return (
                                          <tr
                                            key={instance?.id}
                                            className={cn(
                                              colors.instance,
                                              !isLastInstance && 'border-b border-dotted border-gray-600/50'
                                            )}
                                          >
                                            {/* Instance checkbox */}
                                            <td className="px-4 py-1.5">
                                              <input
                                                type="checkbox"
                                                checked={selectedInstances.has(instance?.id)}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedInstances(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(instance?.id)) {
                                                      next.delete(instance?.id);
                                                    } else {
                                                      next.add(instance?.id);
                                                    }
                                                    return next;
                                                  });
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                              />
                                            </td>
                                            {/* Instance number */}
                                            <td className="px-4 py-1.5 pl-20">
                                              <span className="text-xs text-text-tertiary">
                                                #{instance?.instance_number || instIndex + 1}
                                              </span>
                                            </td>
                                            {/* Contractor */}
                                            <td className="px-4 py-1.5 text-xs text-text-secondary">
                                              {instance?.contractor?.full_name || 'Unassigned'}
                                            </td>
                                            {/* Status */}
                                            <td className="px-4 py-1.5">
                                              <span className={cn(
                                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                                getTimesheetStatusColor(status)
                                              )}>
                                                {formatStatusText(status)}
                                              </span>
                                            </td>
                                            {/* Date */}
                                            <td className="px-4 py-1.5 text-xs text-text-secondary">
                                              {formatDate(instance?.scheduled_date, preferences?.date_format)}
                                            </td>
                                            {/* Hours */}
                                            <td className="px-4 py-1.5 text-xs text-text-secondary">
                                              {formatHoursMinutes(parseFloat(timesheet?.total_hours || 0))}
                                            </td>
                                            {/* Instance actions */}
                                            <td className="px-4 py-1.5">
                                              <div className="flex items-center justify-end gap-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingReport(instance);
                                                  }}
                                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                                  title="View report"
                                                >
                                                  <Eye className="w-3 h-3 text-text-secondary" />
                                                </button>
                                                {status === 'submitted' && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(instance?.id);
                                                      }}
                                                      className="p-1 hover:bg-green-500/20 rounded transition-colors"
                                                      title="Approve"
                                                    >
                                                      <Check className="w-3 h-3 text-green-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRejectingInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                                      title="Reject"
                                                    >
                                                      <XCircle className="w-3 h-3 text-red-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisputingInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-yellow-500/20 rounded transition-colors"
                                                      title="Dispute"
                                                    >
                                                      <AlertTriangle className="w-3 h-3 text-yellow-400" />
                                                    </button>
                                                  </>
                                                )}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingInstanceReport({ job, instance, timesheet });
                                                  }}
                                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                                  title="View job report"
                                                >
                                                  <ClipboardList className="w-3 h-3 text-text-tertiary" />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Report Detail Modal */}
      {viewingReport && (
        <ReportDetailModal
          instance={viewingReport}
          onClose={() => setViewingReport(null)}
          onApprove={() => {
            handleApprove(viewingReport.id);
            setViewingReport(null);
          }}
          onReject={() => {
            setRejectingInstance(viewingReport);
            setViewingReport(null);
          }}
          onDispute={() => {
            setDisputingInstance(viewingReport);
            setViewingReport(null);
          }}
        />
      )}

      {/* Reject Modal */}
      {rejectingInstance && (
        <RejectModal
          instance={rejectingInstance}
          onClose={() => setRejectingInstance(null)}
          onReject={(reason) => handleReject(rejectingInstance.id, reason)}
        />
      )}

      {/* Dispute Modal */}
      {disputingInstance && (
        <DisputeModal
          instance={disputingInstance}
          onClose={() => setDisputingInstance(null)}
          onDispute={(reason, category) => handleDispute(disputingInstance.id, reason, category)}
        />
      )}

      {/* Job Report Modal (for job-level view) */}
      {viewingJobReport && (() => {
        // For one-time jobs (single instance), pass the instance and report data
        const isSingleInstance = viewingJobReport.instances?.length === 1;
        const singleInstance = isSingleInstance ? viewingJobReport.instances[0] : null;
        const singleTimesheet = singleInstance?.timesheet?.[0] || singleInstance?.timesheet;
        const singleOvertimeRequest = singleInstance?.overtime_request?.[0] || singleInstance?.overtime_request;

        return (
          <JobReport
            job={transformJobForReport(viewingJobReport.job, viewingJobReport.instances)}
            instance={isSingleInstance ? transformInstanceForReport(singleInstance) : null}
            company={currentCompany}
            onClose={() => setViewingJobReport(null)}
            // Pass report data for single-instance jobs (one-time jobs)
            timesheet={isSingleInstance ? singleTimesheet : null}
            overtimeRequest={isSingleInstance ? singleOvertimeRequest : null}
            workNotes={isSingleInstance ? singleInstance?.work_notes : null}
            checklistData={isSingleInstance ? singleInstance?.checklist_data : null}
            questionnaireResponses={isSingleInstance ? singleInstance?.questionnaire_responses : null}
            photos={isSingleInstance ? (singleInstance?.photos || []) : []}
            partnerAgency={viewingJobReport.job?.partner_agency}
          />
        );
      })()}

      {/* Instance Report Modal (for instance-level view) */}
      {viewingInstanceReport && (() => {
        const instanceOvertimeRequest = viewingInstanceReport.instance?.overtime_request?.[0] || viewingInstanceReport.instance?.overtime_request;
        return (
          <JobReport
            job={transformJobForReport(viewingInstanceReport.job, [viewingInstanceReport.instance])}
            instance={transformInstanceForReport(viewingInstanceReport.instance)}
            company={currentCompany}
            onClose={() => setViewingInstanceReport(null)}
            // Report data for completed jobs - prefer passed timesheet, then nested timesheet
            timesheet={viewingInstanceReport.timesheet || viewingInstanceReport.instance?.timesheet?.[0] || viewingInstanceReport.instance?.timesheet}
            overtimeRequest={instanceOvertimeRequest}
            workNotes={viewingInstanceReport.instance?.work_notes}
            checklistData={viewingInstanceReport.instance?.checklist_data}
            questionnaireResponses={viewingInstanceReport.instance?.questionnaire_responses}
            photos={viewingInstanceReport.instance?.photos || []}
            partnerAgency={viewingInstanceReport.job?.partner_agency}
          />
        );
      })()}

      {/* Overtime Approval Modal */}
      {reviewingOvertime && (
        <OvertimeApprovalModal
          request={reviewingOvertime}
          onApprove={handleOvertimeApprove}
          onReject={handleOvertimeReject}
          onClose={() => setReviewingOvertime(null)}
          submitting={overtimeProcessing}
        />
      )}
    </div>
  );
}
